import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import { ScrollView } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { PrayRewardCitation } from '@/src/features/pray/ui/components/PrayRewardCitation/PrayRewardCitation'
import { useXMRPMRedirect } from '@/src/features/rosario/services/useXMRPMRedirect'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import type { TRandomPopularIntentionApi } from '../../../api/types/getIntentionApi.types'
import { RandomIntention } from '../../components/RandomIntention/RandomIntention'

export const IntentionCreationRewardScreen = (): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()
  const redirect = useXMRPMRedirect({ destination: routes.intentions() })

  const handleContinue = () => {
    GTM.sendEvent(
      { event: EVENTS.POST_PRAY_INTENTION_POPIN_CONTINUE },
      { trackingSource: TRACKING_SOURCE.INTENTION_CREATION_PAGE },
    )
    if (router.canDismiss()) {
      router.dismissTo(redirect)
    } else {
      router.navigate(redirect)
    }
  }

  const handlePrayPress = (intention: TRandomPopularIntentionApi) => {
    GTM.sendEvent(EVENTS.POST_PRAY_INTENTION_CREATION_PRAY_INTENTION_CLICKED, {
      trackingSource: TRACKING_SOURCE.INTENTION_CREATION_PAGE,
    })
    router.navigate(
      routes.intentionCreationPray({
        id: intention?.id,
        shareToken: intention.shareToken,
        source: TRACKING_SOURCE.INTENTION_CREATION_PAGE,
      }),
    )
  }

  return (
    <ScrollView
      testID={TEST_ID.INTENTIONS.SCREEN.CREATION_REWARD_SCREEN}
      className="flex h-screen min-h-screen w-full flex-col items-center pt-16"
      contentContainerClassName="max-w-full"
    >
      <View className="flex max-w-[500px] flex-col gap-6 px-4">
        <View className="gap-2 px-4 text-center">
          <GradientTypography className="text-2xl font-black sm:text-3xl" colors="primary">
            <FormattedMessage id="intention.created.success" />
          </GradientTypography>
          <Typography colors="primary" className="py-1">
            <FormattedMessage id="intention.created.description" />
          </Typography>
        </View>
        <PrayRewardCitation />
        <RandomIntention
          testID={TEST_ID.INTENTION.REWARD.SCREEN.RANDOM}
          source={TRACKING_SOURCE.INTENTION_CREATION_PAGE}
          onPrayPress={handlePrayPress}
        />
        <View className="my-6 flex h-12 w-full flex-row items-center justify-center py-6">
          <Button
            onPress={handleContinue}
            testID={TEST_ID.REWARD.CONTINUE}
            variant="light"
            colors="primary"
            endIcon={ICON.CHEVRON_RIGHT}
          >
            <FormattedMessage id="word.continue" />
          </Button>
        </View>
      </View>
    </ScrollView>
  )
}
